<template>
  <div id="transaction-history">
    <div id="header-container" class="pl-5">
      <img :src="`${publicPath}/img/gep/bg-header.svg`" class="img-header" />
      <div class="pt-4 text-white">
        <h3 class="text-white">{{ $t("Monthly Statement") }}</h3>
        <span class="fz-16 fw-400">{{ $t("View your statements here") }}</span>
      </div>
    </div>

    <b-container fluid class="mt-0 pb-5">
      <b-row class="pt-3">
        <b-col cols="12">
          <div
            class="block-item p-3 w-100 mb-4"
            style="margin-top: -100px; box-shadow: 0px 5px 10px 0px #0000000d"
          >
            <b-row class="align-items-end">
              <b-col cols="12" md="4" lg="5">
                <label>{{ $t("Transaction Date") }} :</label>
                <date-picker
                  v-model="search.date"
                  type="month"
                  :disabled-date="disabledDate"
                  value-type="YYYY-MM-DD"
                  :editable="false"
                  :placeholder="$t('Select Month')"
                  :lang="getLocalizationsDate()"
                ></date-picker>
              </b-col>

              <b-col cols="12" md="4" lg="5"></b-col>

              <b-col cols="12" md="4" lg="2">
                <button
                  class="btn btn-primary w-100 mb-0 mt-3 mt-md-0"
                  :disabled="isSearchDisabled"
                  @click="doFilter"
                >
                  {{ $t("Search") }}
                </button>
              </b-col>
            </b-row>
          </div>

          <!-- Modal for Bank Statement IFRAME -->
          <b-modal
            id="bank-statement-modal"
            v-model="showBankStatementModal"
            size="xl"
            hide-footer
            centered
            :title="$t('Bank Statement')"
            dialog-class="bank-statement-modal-dialog"
            content-class="bank-statement-modal-content"
          >
            <div
              id="body-container"
              class="w-100 mb-4 mt-1 position-relative"
              style="height: 1000px"
            >
              <b-embed
                class="iframe"
                type="iframe"
                @load="IframeLoaded"
                :src="'bank-statements.html?v='+nocacheVersion"
                allowfullscreen
                style="height: 1000px; width: 100%"
              ></b-embed>
            </div>
          </b-modal>

          <!-- TABEL -->
          <div v-show="isShowTabelSummary" class="mt-4">
            <h5 class="ml-4 mb-2">✦ {{ $t("Account Summary") }}</h5>
            <table class="table">
              <thead class="thead-transparent">
                <tr>
                  <th
                    v-for="field in tableFields"
                    :key="field.key"
                    scope="col"
                    style="text-transform: none !important"
                  >
                    {{ $t(field.label) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, rowIndex) in accountSummary" :key="rowIndex">
                  <td
                    v-for="(field, colIndex) in tableFields"
                    :key="field.key"
                    class="text-center"
                  >
                    <span v-if="colIndex !== tableFields.length - 1">
                      {{ item[field.key] }}
                    </span>
                    <span v-else>
                      <span v-if="item.unavailable">{{ $t('Unavailable') }}</span>
                      <button
                        v-else
                        class="btn btn-success btn-sm"
                        @click="viewPDF(item)"
                      >
                        {{ $t("View") }}
                      </button>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <LoaderComponent v-if="isLoading"></LoaderComponent>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import enEN from "vue2-datepicker/locale/en";
import zhCN from "vue2-datepicker/locale/zh-cn";
import zhTW from "vue2-datepicker/locale/zh-tw";
import jaJA from "vue2-datepicker/locale/ja";

import LoaderComponent from "@/components/LoaderComponent";

import Account from "@/services/Account";
import CreateAccount from "@/services/CreateAccount";
import Transaction from "@/services/Transaction";
import Common from "@/services/Common";

const USER_TYPE = {
  2: "China Enterprise",
  3: "Hong Kong Enterprise",
  5: "International Enterprise",
};

export default {
  name: "Statements",
  data() {
    return {
      publicPath: process.env.BASE_URL !== "/" ? process.env.BASE_URL : "",
      isDebug: process.env.VUE_APP_DEBUG_MODE,
      userListDebug: process.env.VUE_APP_LIST_USER_DEBUG,
      nocacheVersion: process.env.VUE_APP_NOCACHE_VERSION,
      isLoading: false,
      tmpCurrencyList: [],
      currencyList: [],
      dataAccount: null,
      search: {
        date: "",
        ccy: "",
      },
      isShowIframe: false,
      showBankStatementModal: false, // Modal visibility
      bankStatement: {
        company: null,
        statements: null,
        from_date: null,
        to_date: null,
        currency: null,
        t_download_pdf: this.$t("Download PDF"),
      },
      accountSummary: [],
      tableFields: [
        { key: "currency", label: "Currency" },
        { key: "balance_start", label: "Opening Balance" },
        { key: "credit_total", label: "Total Credit" },
        { key: "debit_total", label: "Total Debit" },
        { key: "balance_end", label: "Closing Balance" },
        { key: "action", label: "Action" },
      ],
      isShowTabelSummary: false,
    };
  },
  components: {
    DatePicker,
    LoaderComponent,
  },
  computed: {
    isSearchDisabled() {
      return !this.search.date;
    },
  },
  watch: {
    "search.date"(newValue) {
      if (!newValue) {
        this.isShowTabelSummary = false;
      }
    },
  },
  methods: {
    getLocalizationsDate() {
      const valueLang = localStorage.getItem("lang");
      // console.log("lang = ", valueLang);

      if (valueLang) {
        if (valueLang === "zh-CN") {
          const customZh = {
            ...zhCN,
            months: [
              "一月",
              "二月",
              "三月",
              "四月",
              "五月",
              "六月",
              "七月",
              "八月",
              "九月",
              "十月",
              "十一月",
              "十二月",
            ],
            monthsShort: [
              "一月",
              "二月",
              "三月",
              "四月",
              "五月",
              "六月",
              "七月",
              "八月",
              "九月",
              "十月",
              "十一月",
              "十二月",
            ],
          };
          return customZh;
        } else if (valueLang === "zh-TW") {
          const customTw = {
            ...zhTW,
            months: [
              "一月",
              "二月",
              "三月",
              "四月",
              "五月",
              "六月",
              "七月",
              "八月",
              "九月",
              "十月",
              "十一月",
              "十二月",
            ],
            monthsShort: [
              "一月",
              "二月",
              "三月",
              "四月",
              "五月",
              "六月",
              "七月",
              "八月",
              "九月",
              "十月",
              "十一月",
              "十二月",
            ],
          };
          return customTw;
        } else if (valueLang === "ja-JA") {
          return jaJA;
        } else {
          return enEN;
        }
      } else {
        return enEN;
      }
    },
    disabledDate(date) {
      const startAprilDate = new Date();
      startAprilDate.setFullYear(2025, 3, 1);
      startAprilDate.setHours(0, 0, 0, 0);
      return date < startAprilDate;
    },
    getTransaction() {
      const self = this;
      CreateAccount.TransactionList()
        .then((response) => {
          if (response.status == 200) {
            const responseData = response.data;
            const data = responseData.data;

            if (data) {
              const usedCurrencies = new Set(data.map((item) => item.ccy));
              for (let i = self.tmpCurrencyList.length - 1; i >= 0; i--) {
                const ccy = self.tmpCurrencyList[i];
                if (usedCurrencies.has(ccy)) {
                  self.tmpCurrencyList.splice(i, 1);
                }
              }
            }

            self.getBalances();
          } else {
            self.isLoading = false;
          }
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getBalances() {
      const self = this;
      const balancePromises = self.tmpCurrencyList.map((ccy) => {
        return Account.getBalance({
          userNo: self.dataAccount.userNo,
          ccy: ccy,
        }).then((response) => {
          const data = response.data.data.data;

          if (data) {
            const balance = data.availableBal;
            if (balance > 0) {
              // jika balancenya ada
              // maka hapus salah satu currency di tmpCurrencyList
              self.tmpCurrencyList.splice(self.tmpCurrencyList.indexOf(ccy), 1);
            }
          }
        });
      });

      Promise.all(balancePromises)
        .then(() => {
          self.filterCurrency();
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    filterCurrency() {
      const self = this;
      self.tmpCurrencyList.forEach((item) => {
        const index = self.currencyList.indexOf(item);
        if (index > -1) {
          self.currencyList.splice(index, 1);
        }
      });
    },
    getAccount() {
      const self = this;
      CreateAccount.list({
        search: self.search,
        start: 0,
        length: -1,
      })
        .then((response) => {
          if (response.status == 200) {
            const responseData = response.data;
            self.dataAccount = responseData.data[0];

            if (self.dataAccount) {
              const userType = self.dataAccount.userType;

              if (userType == 5) {
                self.bankStatement.company = {
                  province: self.dataAccount.information.registerProvince,
                  city: self.dataAccount.information.registerCity,
                  district: self.dataAccount.information.registerArea,
                };
              }

              self.getSubAccount();
            } else {
              self.isLoading = false;
            }
          } else {
            self.isLoading = false;
          }
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getSubAccount() {
      let self = this;
      CreateAccount.listSubAccount({
        start: 0,
        length: -1,
        search: { value: self.dataAccount.userNo },
      })
        .then((response) => {
          if (response.status == 200) {
            const responseData = response.data;

            if (responseData.data.length > 0) {
              const data = responseData.data[0];

              if (data) {
                // passing list currency
                self.tmpCurrencyList = data.currency.split(",");
                self.currencyList = self.tmpCurrencyList.map((item) =>
                  item.trim()
                );
                self.bankStatement.company = {
                  ...self.bankStatement.company,
                  account_name: self.dataAccount.information.enName,
                  address: self.dataAccount.information.address,
                  account_type: USER_TYPE[self.dataAccount.userType],
                  bank_account_name: data.bankAccountName,
                  bank_name: data.bankName,
                  bank_address: data.bankAddress,
                  bank_code: data.bankCode,
                  branch_code: data.bankSubCode,
                  account_no: data.bankNo,
                  // 'open_date': data.created_at ? moment(data.created_at).format('DD/MM/YYYY') : null,
                };

                switch (self.dataAccount.userType) {
                  case 2:
                    self.getProvince("CHN");
                    break;
                  case 3:
                    self.getProvince("HKG");
                    break;
                  default:
                    const countryCode =
                      self.dataAccount.information.registerCountry;
                    self.getCountry(countryCode);
                    break;
                }

                self.getTransaction();
              } else {
                self.isLoading = false;
              }
            } else {
              self.isLoading = false;
            }
          } else {
            self.isLoading = false;
          }
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getCountry(countryCode) {
      let self = this;
      Common.countriesByCode({ country_code: countryCode })
        .then((response) => {
          const data = response.data.data;

          if (response.status == 200) {
            if (data) {
              self.bankStatement.company.country = data[0].name;
            }
          }

          self.isLoading = false;
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getProvince(countryCode) {
      let self = this;

      CreateAccount.getProvince({ country: countryCode })
        .then((response) => {
          const data = response.data.data;

          if (response.status == 200) {
            if (data) {
              const information = self.dataAccount.information;

              if (information) {
                if (self.dataAccount.userType !== 5) {
                  // find province
                  const idRegisterProvince =
                    information.registerProvince.split("_")[0];
                  const dataProvince = data.find(
                    (item) => item.id == idRegisterProvince
                  );
                  self.bankStatement.company.province =
                    dataProvince.english_text;

                  if (idRegisterProvince) {
                    self.getCity(idRegisterProvince);
                  } else {
                    self.isLoading = false;
                  }
                } else {
                  self.bankStatement.company.province =
                    information.registerProvince;
                  self.bankStatement.company.city = information.registerCity;
                  self.bankStatement.company.district =
                    information.registerArea;
                  self.isLoading = false;
                }
              } else {
                self.isLoading = false;
              }
            } else {
              self.isLoading = false;
            }
          } else {
            self.isLoading = false;
          }
        })
        .catch((err) => {
          this.checkError(err);
        });
    },
    getCity(provinceCode) {
      let self = this;
      CreateAccount.getCity({ province_id: provinceCode })
        .then((response) => {
          const data = response.data.data;

          if (response.status == 200) {
            if (data) {
              const information = self.dataAccount.information;
              if (information) {
                // find city
                const idRegisterCity = information.registerCity.split("_")[0];
                const dataCity = data.find(
                  (item) => item.code == idRegisterCity
                );
                self.bankStatement.company.city = dataCity.english_text;

                if (idRegisterCity) {
                  self.getDistrict(idRegisterCity);
                } else {
                  self.isLoading = false;
                }
              } else {
                self.isLoading = false;
              }
            } else {
              self.isLoading = false;
            }
          } else {
            self.isLoading = false;
          }
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getDistrict(cityCode) {
      let self = this;
      CreateAccount.getDistrict({ city_id: cityCode })
        .then((response) => {
          const data = response.data.data;

          if (response.status == 200) {
            if (data) {
              const information = self.dataAccount.information;
              if (information) {
                // find district
                const idRegisterDistrict =
                  information.registerArea.split("_")[0];
                const dataDistrict = data.find(
                  (item) => item.id == idRegisterDistrict
                );
                self.bankStatement.company.district = dataDistrict.english_text;
              }
            }
          }

          self.isLoading = false;
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getBankStatement() {
      const self = this;
      Transaction.statements(self.search)
        .then((response) => {
          if (response.status == 200) {
            const responseData = response.data.data;

            //if (responseData.transactions.length>0) {
            // if (parseFloat(responseData.opening_balance) > 0) {
              const firstTransaction = moment(self.search.date)
                .startOf("month")
                .format("YYYY-MM-DD");

              const createdTime = moment(
                firstTransaction,
                "YYYY-MM-DD HH:mm:ss"
              );
              const lastDateOfPreviousMonth = createdTime
                .clone()
                .subtract(1, "month")
                .endOf("month")
                .format("YYYY-MM-DD HH:mm:ss");

              const openingTransaction = {
                id: -1,
                trans_no: "",
                userno: firstTransaction.userno,
                account_id: firstTransaction.account_id,
                currency: firstTransaction.currency,
                amount: "",
                data_type: "",
                entity_type: "",
                entity_id: null,
                created_by: 0,
                created_time: lastDateOfPreviousMonth,
                updated_by: null,
                updated_time: null,
                balance: responseData.opening_balance,
                balance_outstanding: responseData.opening_balance,
                description: "承上結餘 Balance Brought Forward",
              };

              responseData.transactions.unshift(openingTransaction);
            // }

            self.bankStatement.statements = responseData;
            self.bankStatement.from_date = moment(self.search.date)
              .startOf("month")
              .format("DD/MM/YYYY");
            self.bankStatement.to_date = moment(self.search.date)
              .endOf("month")
              .format("DD/MM/YYYY");

            // Tampilkan modal, iframe akan diisi setelah modal benar-benar tampil
            self.showBankStatementModal = true;
          }

          self.isLoading = false;
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    getWalletReport() {
      const self = this;
      self.isLoading = true;

      const params = {
        date: self.search.date,
      };

      Transaction.walletReport(params)
        .then((response) => {
          if (response.status == 200 && response.data.success) {
            const responseData = response.data.data.data; // Access nested "data.data"

            // Filter responseData to only include keys matching tableFields
            const allowedKeys = self.tableFields.map((field) => field.key);
            self.accountSummary = responseData.map((item) => {
              const filteredItem = Object.keys(item)
                .filter((key) => allowedKeys.includes(key))
                .reduce((result, key) => {
                  if (key === "currency") {
                    result[key] = item[key];
                  } else {
                    result[key] =
                      isNaN(item[key]) || item[key] === null
                        ? "0.00"
                        : parseFloat(item[key]).toFixed(2);
                  }
                  return result;
                }, {});

              // --- Cek bulan dari item vs filter (self.search.date) ---
              const selectedDate = new Date(self.search.date);
              const today = new Date();

              if (!isNaN(selectedDate.getTime())) {
                const sameMonth =
                  selectedDate.getMonth() === today.getMonth() &&
                  selectedDate.getFullYear() === today.getFullYear();

                if (sameMonth) {
                  filteredItem["unavailable"] = true;
                }
              } else {
                // Tanggal tidak valid
                filteredItem["unavailable"] = true;
                console.warn("Tanggal tidak valid:", self.search.date);
              }


              return filteredItem;
            });
          }

          console.log(self.accountSummary);

          self.isLoading = false;
          self.isShowTabelSummary = true;
        })
        .catch((err) => {
          self.checkError(err);
        });
    },
    doFilter() {
      const self = this;
      self.isLoading = true;
      self.isShowIframe = false; // Ensure iframe is hidden
      self.isShowTabelSummary = false;
      self.bankStatement.currency = self.search.ccy;

      self.getWalletReport();
    },
    IframeLoaded() {
      this.sendFilterToIframe();
    },
    sendFilterToIframe() {
      const self = this;
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: "RENDER", data: self.bankStatement },
          "*"
        );
        self.isShowIframe = true;
      }
    },
    checkError(err) {
      this.isLoading = false;
      const response = err.response;
      try {
        if (response.status == 401 && response.data.name == "Unauthorized") {
          localStorage.removeItem("token");
          localStorage.removeItem("member");
          localStorage.removeItem("isFirstTime");
          this.$router.push({
            path: "/login",
          });
        } else {
          if (localStorage.getItem("token")) {
            var msg = response.data ? response.data.message : err.message;
            this.$notify({
              type: "warn",
              text: msg,
            });
          }
        }
      } catch (e) {
        console.log(err);
      }
    },
    viewPDF(item) {
      const self = this;
      self.isLoading = true;
      self.search.ccy = item.currency; // Set the currency based on the selected row
      self.getBankStatement(); // Fetch the report statement for the selected currency
      // self.showBankStatementModal = true; // <-- Hapus dari sini
    },
    getProfile() {
      Common.profile().then((response) => {
          if (response.status == 200 && response.data.success) {
              const profile = response.data.data;

              if (
              (!this.userListDebug.includes(profile.username) && this.isDebug == 1)
              ) {
                  this.$router.push({ 
                      path: '/bank-statements', 
                  });
              }
          }
      });
    }
  },

  mounted() {
    let self = this;
    this.$nextTick(() => {
      self.isLoading = true;
      self.getProfile();
      self.getAccount();
    });
  },
};
</script>

<style>
#body-container {
  height: 1000px;
}

.mx-datepicker .mx-input {
  height: 48.4px !important;
  line-height: 40px;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 8px;
}

.embed-responsive {
  height: 100%;
}

.iframe {
  display: block !important;
}

.thead-transparent th {
  background-color: #ffffff !important;
  color: #000000 !important;
  font-weight: bold !important;
  text-transform: none !important;
}

.bank-statement-modal-dialog {
  max-width: 100vw;
  width: 100%;
}

.bank-statement-modal-content {
  padding: 0;
  background: transparent;
}

#bank-statement-modal .modal-content {
  border-radius: 12px;
  overflow: hidden;
}

#bank-statement-modal .modal-body {
  padding: 0;
}

/* Updated styles for the Bank Statement header */
#bank-statement-modal .modal-header {
  background-color: #ffffff !important; /* Solid white background */
  color: #000000 !important; /* Black text color */
  font-weight: bold !important;
  border: 2px solid #ddd;
  border-radius: 10px; /* Border radius untuk segala sisi */
}
</style>
